import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle, 
  FileText,
  TrendingUp,
  Search,
  Filter,
  Eye,
  UserCheck,
  UserX,
  MessageSquare,
  Headphones,
  ArrowRight
} from 'lucide-react';
import { useAdmin } from '@hooks/useAdmin';
import { useAuth } from '@hooks/useAuth';
import { ROUTES, USER_ROLES } from '@constants';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getDashboardStats, getMentorList, loading } = useAdmin();

  const [stats, setStats] = useState(null);
  const [mentors, setMentors] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    status: 'pending',
    search: '',
    page: 1,
    limit: 10
  });

  useEffect(() => {
    if (user?.role !== USER_ROLES.ADMIN) {
      toast.error('Access denied. Admin only.');
      navigate(ROUTES.DASHBOARD);
      return;
    }

    fetchDashboardData();
  }, [user, navigate]);

  useEffect(() => {
    fetchMentors();
  }, [filters]);

  const fetchDashboardData = async () => {
    try {
      const statsResponse = await getDashboardStats();
      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats', error);
    }
  };

  const fetchMentors = async () => {
    try {
      const response = await getMentorList(filters);
      if (response.success) {
        setMentors(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch mentors', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
      not_submitted: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Not Submitted' }
    };
    const badge = badges[status] || badges.not_submitted;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getDocumentStats = (documents) => {
    const total = documents?.length || 0;
    const approved = documents?.filter(d => d.status === 'approved').length || 0;
    const pending = documents?.filter(d => d.status === 'pending').length || 0;
    const rejected = documents?.filter(d => d.status === 'rejected').length || 0;
    return { total, approved, pending, rejected };
  };

  if (!stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 p-6">
        {/* Header */}
        {/* <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary-900 mb-2">
            Admin Dashboard
          </h1>
          <p className="text-primary-600">
            Manage mentor verifications and platform statistics
          </p>
        </div> */}

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/feedback')}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <MessageSquare className="w-6 h-6 text-primary-600" />
                  <h3 className="text-lg font-bold text-primary-900">User Feedback</h3>
                </div>
                <p className="text-sm text-primary-600 mb-3">
                  Review and manage user feedback submissions
                </p>
                <Button variant="outline" size="sm">
                  View Feedback <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/admin/support')}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Headphones className="w-6 h-6 text-primary-600" />
                  <h3 className="text-lg font-bold text-primary-900">Support Tickets</h3>
                </div>
                <p className="text-sm text-primary-600 mb-3">
                  Manage and respond to user support tickets
                </p>
                <Button variant="outline" size="sm">
                  View Tickets <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary-600 mb-1">Total Mentors</p>
                <p className="text-3xl font-bold text-primary-900">{stats.totalMentors}</p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary-600 mb-1">Pending Review</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pendingMentors}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary-600 mb-1">Approved</p>
                <p className="text-3xl font-bold text-green-600">{stats.approvedMentors}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary-600 mb-1">Pending Documents</p>
                <p className="text-3xl font-bold text-blue-600">{stats.pendingDocuments}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>
            <div className="flex gap-2">
              {['all', 'pending', 'approved', 'rejected'].map((status) => (
                <Button
                  key={status}
                  variant={filters.status === status ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => handleFilterChange('status', status)}
                  className="capitalize"
                >
                  {status}
                </Button>
              ))}
            </div>
          </div>
        </Card>

        {/* Mentor List */}
        <Card className="overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-primary-900">
              Mentor Applications
            </h2>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : mentors.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-primary-300 mx-auto mb-4" />
              <p className="text-primary-600">No mentors found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-primary-600 uppercase tracking-wider">
                        Mentor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-primary-600 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-primary-600 uppercase tracking-wider">
                        Documents
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-primary-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-primary-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mentors.map((mentor) => {
                      const docStats = getDocumentStats(mentor.documents);
                      return (
                        <tr key={mentor.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-semibold">
                                {mentor.email?.[0]?.toUpperCase()}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-primary-900">
                                  {mentor.email}
                                </div>
                                <div className="text-sm text-primary-500">
                                  ID: {mentor.id.slice(0, 8)}...
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-primary-900">{mentor.email}</div>
                            <div className="text-sm text-primary-500">{mentor.phone || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex gap-2 text-xs">
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                ✓ {docStats.approved}
                              </span>
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                                ⏳ {docStats.pending}
                              </span>
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                                ✗ {docStats.rejected}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(mentor.adminApproved)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/admin/mentors/${mentor.id}`)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Review
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="p-6 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-primary-600">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Previous
                    </Button>
                    {[...Array(pagination.totalPages)].map((_, i) => (
                      <Button
                        key={i + 1}
                        variant={pagination.page === i + 1 ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </Button>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
    </div>
  );
};

export default AdminDashboard;
